class CartCheckboxUpsell {
  constructor(cartItemsInstance) {
    this.cartItemsInstance = cartItemsInstance;
    this.baseUrl = "/cart";
    this.initEventDelegation();
    document.addEventListener("cartUpdated", this.handleCartUpdate.bind(this));
    this.updateCheckboxStates();
  }

  initEventDelegation() {
    document.body.addEventListener("change", async (event) => {
      if (event.target.classList.contains("cart-addon__checkbox")) {
        const checkbox = event.target;
        const isChecked = checkbox.checked;
        const container = checkbox.closest(".cart-addon");
        const variantId = checkbox.getAttribute("data-variant-id");

        if (isChecked) {
          await this.addUpsellCheckboxToCart(variantId);
          container.classList.add("cart-addon--checked");
          checkbox.disabled = true; // Disable the checkbox
        } else {
          await this.removeUpsellCheckboxFromCart(variantId);
          container.classList.remove("cart-addon--checked");
          checkbox.disabled = false; // Enable the checkbox
        }
      }
    });
  }

  async getVariantIdByPrice(productHandle, price) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const product = await response.json();
      const priceInCents = Math.round(price * 100); // Convert price to cents for comparison

      const variant = product.variants.find((v) => v.price === priceInCents);
      return variant ? variant.id : null;
    } catch (error) {
      console.error("Error fetching product for price lookup:", error);
      return null;
    }
  }

  async getProductDetails(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok)
        throw new Error(
          `Failed to fetch product details: ${response.statusText}`
        );

      const product = await response.json();
      return product;
    } catch (error) {
      console.error(
        `Error fetching product details for handle ${handle}:`,
        error
      );
      return null;
    }
  }

  async updateCheckboxStates() {
    try {
      const response = await fetch(`${this.baseUrl}.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.statusText}`);
      }
      const cart = await response.json();

      // Gather non-upsell items
      const nonUpsellItems = [];
      for (const item of cart.items) {
        const product = await this.getProductDetails(item.handle);
        if (product && !product.tags.includes("upsell")) {
          nonUpsellItems.push(item);
        } else {
          console.log(`Skipping upsell product: ${item.handle}`);
        }
      }

      // Calculate total quantity of non-upsell items
      const totalNonUpsellQuantity = nonUpsellItems.reduce(
        (sum, item) => sum + item.quantity,
        0
      );

      for (const item of nonUpsellItems) {
        const variantId = item.variant_id;
        const productHandle = item.handle;

        document.querySelectorAll(".cart-addon").forEach(async (addonItem) => {
          const checkboxProductHandle = addonItem.getAttribute(
            "data-product-handle"
          );
          let basePrice =
            parseFloat(addonItem.getAttribute("data-upsell-price")) / 100 || 0;

          const newPrice = basePrice + basePrice * (totalNonUpsellQuantity - 1);
          const formattedPrice = "£" + newPrice.toFixed(2);

          const priceSpan = addonItem.querySelector(".cart-addon__price");
          const checkbox = addonItem.querySelector(".cart-addon__checkbox");

          if (priceSpan) {
            priceSpan.innerText = formattedPrice;
            checkbox.setAttribute("data-price", newPrice.toFixed(2));
          }

          const variantIdForCheckbox = await this.getVariantIdByPrice(
            checkboxProductHandle,
            newPrice
          );
          if (variantIdForCheckbox) {
            checkbox.setAttribute("data-variant-id", variantIdForCheckbox);

            const hasUpsellCheckbox = cart.items.some(
              (cartItem) =>
                cartItem.variant_id.toString() === variantIdForCheckbox &&
                !cartItem.product.tags.includes("upsell")
            );

            checkbox.checked = hasUpsellCheckbox;
            checkbox.disabled = hasUpsellCheckbox;
            addonItem.classList.toggle(
              "cart-addon--checked",
              hasUpsellCheckbox
            );
          } else {
            console.error("Variant not found for price:", newPrice);
          }
        });
      }
    } catch (error) {
      console.error("Error updating checkbox states:", error);
    }
  }

  async addUpsellCheckboxToCart(variantId) {
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", 1);

    try {
      const response = await fetch(`${this.baseUrl}/add.js`, {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        console.log("Upsell item added to cart successfully.");
        if (
          this.cartItemsInstance &&
          typeof this.cartItemsInstance.onCartUpdate === "function"
        ) {
          this.cartItemsInstance.onCartUpdate();
          this.onCartUpdateCheckboxUpsell();
        }
      } else {
        console.error("Failed to add upsell item to cart.");
      }
      await response.json();
    } catch (error) {
      console.error("Error adding cart upsell:", error);
    }
  }

  async removeUpsellCheckboxFromCart(variantId) {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      const lineItemIndex = cart.items.findIndex(
        (item) => item.variant_id.toString() === variantId
      );
      if (lineItemIndex !== -1) {
        const line = lineItemIndex + 1;
        await this.cartItemsInstance.updateQuantity(line, 0, null, null);

        // Update only the relevant checkbox
        const checkbox = document.querySelector(
          `.cart-addon__checkbox[data-variant-id="${variantId}"]`
        );
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = false;
          const container = checkbox.closest(".cart-addon");
          container.classList.remove("cart-addon--checked");
        } else {
          console.log(`No upsell checkbox found for variantId: ${variantId}`);
        }
      } else {
        console.error("Cart upsell product not found in cart.");
      }
    } catch (error) {
      console.error("Error fetching cart for cart upsell removal:", error);
    }
  }

  getSectionsToRenderCheckboxUpsell() {
    const mainCartFooter = document.getElementById("main-cart-footer");
    const sections = [
      ...(mainCartFooter
        ? [
            {
              id: "main-cart-footer",
              section: mainCartFooter.dataset.id,
              selector: "#main-cart-footer .js-contents",
            },
          ]
        : []),
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: "#cart-icon-bubble",
      },
    ];
    return sections;
  }

  onCartUpdateCheckboxUpsell() {
    this.getSectionsToRenderCheckboxUpsell().forEach((section) => {
      fetch(`${routes.cart_url}?section_id=${section.section}`)
        .then((response) => response.text())
        .then((html) => {
          const container = document.querySelector(section.selector);
          if (!container) {
            console.error(
              `Container not found for selector: ${section.selector}`
            );
            return;
          }
          if (section.id === "main-cart-footer") {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const jsContents = doc.querySelector(".js-contents");

            if (jsContents) {
              container.innerHTML = jsContents.innerHTML;
            } else {
              console.error(
                `.js-contents not found in fetched HTML for section: ${section.id}`
              );
            }
          } else if (section.id === "cart-icon-bubble") {
            container.innerHTML = html;
          }
        })
        .catch((error) =>
          console.error(`Error updating section ${section.id}:`, error)
        );
    });
  }

  handleCartUpdate() {
    setTimeout(() => this.updateCheckboxStates(), 500);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const cartItemsInstance = document.querySelector("cart-items");
  const cartDrawerItemsInstance = document.querySelector("cart-drawer-items");
  const activeCartInstance = cartDrawerItemsInstance || cartItemsInstance;

  if (activeCartInstance) {
    new CartCheckboxUpsell(activeCartInstance);
  } else {
    console.log("Neither cart-items nor cart-drawer-items found in the DOM.");
  }
});
